import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useAppSelector } from '../../store/hooks';
import useRefund from '../../jetSetter/hooks/useRefund';
import RefundApprovalModal from '../components/RefundApprovalModal';

const RefundManagementPage = () => {
  const { user } = useAppSelector((state) => state.auth);
  const isSubAdmin = user?.user_type === 'SubAdmin';
  const isFullAdmin = user?.user_type === 'Admin';
  const adminCanReview = (status) =>
    ['Pending Approval', 'Awaiting SubAdmin Approval', 'Awaiting Admin Approval'].includes(
      status
    );
  const subAdminCanReview = (status) => status === 'Pending Approval';
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [approvalType, setApprovalType] = useState(null); // 'subadmin' or 'admin'

  const { getPendingRefunds, getRefundDetails } = useRefund();

  useEffect(() => {
    loadRefunds();
  }, [currentPage, limit]);

  const loadRefunds = async () => {
    setLoading(true);
    try {
      const result = await getPendingRefunds(currentPage, limit);
      if (result) {
        setRefunds(result.refund_requests);
        setPagination(result.pagination);
      }
    } catch (error) {
      toast.error('Failed to load refund requests');
      console.error('Load refunds error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenApprovalModal = async (refund, type) => {
    const details = await getRefundDetails(refund.id);
    if (details) {
      setSelectedRefund(details);
      setApprovalType(type);
      setApprovalModalOpen(true);
    }
  };

  const handleCloseApprovalModal = () => {
    setApprovalModalOpen(false);
    setSelectedRefund(null);
    setApprovalType(null);
  };

  const handleApprovalSuccess = () => {
    handleCloseApprovalModal();
    loadRefunds();
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending Approval':
        return 'bg-amber-100 text-amber-800';
      case 'Awaiting Admin Approval':
      case 'Awaiting SubAdmin Approval':
        return 'bg-blue-100 text-blue-800';
      case 'Processing':
        return 'bg-cyan-100 text-cyan-800';
      case 'Completed':
        return 'bg-green-100 text-green-800';
      case 'Rejected':
        return 'bg-red-100 text-red-800';
      case 'Failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-full mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Refund Management</h1>
          <p className="text-gray-600 mt-2">Review and approve pending refund requests</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow">
            <p className="text-gray-600 text-sm">Total Pending</p>
            <p className="text-2xl font-bold text-amber-600">{pagination?.total || 0}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <p className="text-gray-600 text-sm">Awaiting SubAdmin</p>
            <p className="text-2xl font-bold text-amber-600">
              {refunds.filter(r => r.status === 'Pending Approval').length}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <p className="text-gray-600 text-sm">Awaiting Admin</p>
            <p className="text-2xl font-bold text-blue-600">
              {refunds.filter(r => r.status === 'Awaiting Admin Approval').length}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <p className="text-gray-600 text-sm">Processing</p>
            <p className="text-2xl font-bold text-cyan-600">
              {refunds.filter(r => r.status === 'Processing').length}
            </p>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block">
                <i className="fa fa-spinner fa-spin text-2xl text-jet-yellow"></i>
                <p className="text-gray-600 mt-2">Loading refund requests...</p>
              </div>
            </div>
          ) : refunds.length === 0 ? (
            <div className="p-8 text-center">
              <i className="fa fa-inbox text-4xl text-gray-300 mb-4"></i>
              <p className="text-gray-600">No pending refund requests</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Booking Ref</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Jetsetter</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Amount</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Status</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Approvals</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Requested</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {refunds.map((refund) => (
                      <tr key={refund.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                          {refund.booking_reference}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {refund.user_name}
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                          {formatCurrency(refund.refund_amount)}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(refund.status)}`}>
                            {refund.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {refund.approvals_received}/{refund.approvals_required}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {formatDate(refund.requested_at)}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <div className="flex gap-2">
                            {isFullAdmin && adminCanReview(refund.status) && (
                              <button
                                onClick={() => handleOpenApprovalModal(refund, 'admin')}
                                className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white text-xs font-semibold rounded transition-colors"
                              >
                                Admin Review
                              </button>
                            )}
                            {isSubAdmin && subAdminCanReview(refund.status) && (
                              <button
                                onClick={() => handleOpenApprovalModal(refund, 'subadmin')}
                                className="px-3 py-1 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold rounded transition-colors"
                              >
                                SubAdmin Review
                              </button>
                            )}
                            <button
                              onClick={() => handleOpenApprovalModal(refund, 'view')}
                              className="px-3 py-1 bg-gray-500 hover:bg-gray-600 text-white text-xs font-semibold rounded transition-colors"
                            >
                              View Details
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <div className="px-6 py-4 border-t flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Showing {refunds.length} of {pagination.total} refund requests
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-1 text-sm">
                      Page {currentPage} of {pagination.totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))}
                      disabled={currentPage === pagination.totalPages}
                      className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Approval Modal */}
      <RefundApprovalModal
        refund={selectedRefund}
        approvalType={approvalType}
        isOpen={approvalModalOpen}
        onClose={handleCloseApprovalModal}
        onSuccess={handleApprovalSuccess}
      />
    </div>
  );
};

export default RefundManagementPage;
